import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  fetchGastosMes,
  fetchIngresosMes,
  fetchSaldoDisponible,
  fetchAhorros,
  fetchTransaccionesDetalle,
} from '../services/dashboard';
import { monthRange, currentMonthRef } from '../utils/formatters';

function aggregateAhorros(items) {
  if (!Array.isArray(items)) return { total: 0, operaciones: 0 };
  return items.reduce(
    (acc, item) => ({
      total: acc.total + Number(item.sumaMonto ?? 0),
      operaciones: acc.operaciones + Number(item.cantidadOperaciones ?? 0),
    }),
    { total: 0, operaciones: 0 },
  );
}

function useResource(fetchFn) {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const cargar = useCallback(
    async ({ feDesde, feHasta, cdPresupuesto, signal } = {}) => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await fetchFn({ feDesde, feHasta, cdPresupuesto, signal });
        setData(result);
      } catch (err) {
        if (err.name !== 'AbortError') {
          setError(err);
          setData(null);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [fetchFn],
  );

  return useMemo(
    () => ({ data, isLoading, error, cargar }),
    [data, isLoading, error, cargar],
  );
}

export function useDashboardData(cdPresupuesto = null) {
  const initial = useMemo(() => currentMonthRef(), []);
  const [year, setYear] = useState(initial.year);
  const [month, setMonth] = useState(initial.month);

  const range = useMemo(() => monthRange(year, month), [year, month]);
  const cdPresupuestoKey = cdPresupuesto ?? null;

  const {
    data: gastosData,
    isLoading: isLoadingGastos,
    error: gastosError,
    cargar: cargarGastos,
  } = useResource(fetchGastosMes);
  const {
    data: ingresosData,
    isLoading: isLoadingIngresos,
    error: ingresosError,
    cargar: cargarIngresos,
  } = useResource(fetchIngresosMes);
  const {
    data: saldoData,
    isLoading: isLoadingSaldo,
    error: saldoError,
    cargar: cargarSaldo,
  } = useResource(fetchSaldoDisponible);
  const {
    data: ahorrosData,
    isLoading: isLoadingAhorros,
    error: ahorrosError,
    cargar: cargarAhorros,
  } = useResource(fetchAhorros);
  const {
    data: detalleData,
    isLoading: isLoadingDetalle,
    error: detalleError,
    cargar: cargarDetalle,
  } = useResource(fetchTransaccionesDetalle);

  const cargarTodo = useCallback(() => {
    const params = {
      feDesde: range.feDesde,
      feHasta: range.feHasta,
      cdPresupuesto: cdPresupuestoKey,
    };
    cargarGastos(params);
    cargarIngresos(params);
    cargarSaldo(params);
    cargarAhorros(params);
    cargarDetalle(params);
  }, [
    range.feDesde,
    range.feHasta,
    cdPresupuestoKey,
    cargarGastos,
    cargarIngresos,
    cargarSaldo,
    cargarAhorros,
    cargarDetalle,
  ]);

  useEffect(() => {
    cargarTodo();
  }, [cargarTodo]);

  const ahorrosResumen = useMemo(
    () => aggregateAhorros(ahorrosData),
    [ahorrosData],
  );

  const isLoading =
    isLoadingGastos ||
    isLoadingIngresos ||
    isLoadingSaldo ||
    isLoadingAhorros ||
    isLoadingDetalle;
  const hasError = Boolean(
    gastosError || ingresosError || saldoError || ahorrosError || detalleError,
  );

  const retroceder = () => {
    if (month === 0) {
      setYear((y) => y - 1);
      setMonth(11);
    } else {
      setMonth((m) => m - 1);
    }
  };

  const avanzar = () => {
    if (month === 11) {
      setYear((y) => y + 1);
      setMonth(0);
    } else {
      setMonth((m) => m + 1);
    }
  };

  const irMesActual = () => {
    setYear(initial.year);
    setMonth(initial.month);
  };

  const isCurrentMonth = year === initial.year && month === initial.month;

  return {
    range,
    year,
    month,
    isCurrentMonth,
    retroceder,
    avanzar,
    irMesActual,
    gastos: gastosData,
    ingresos: ingresosData,
    saldo: saldoData,
    ahorros: ahorrosData,
    ahorrosResumen,
    detalle: Array.isArray(detalleData) ? detalleData : [],
    isLoading,
    hasError,
    recargar: cargarTodo,
  };
}